/**
 * Type definitions for Group Sessions
 * Ensures type safety when working with group session data throughout the application
 */

import { GroupSessionStatus } from './enums';

/**
 * Group Session Role Enum
 * NOTE: This is being deprecated in favor of the enum in /src/types/enums.ts
 * Kept temporarily for backward compatibility during migration
 * 
 * Represents the role of a user within a specific group session
 * Database column: group_session_members.role
 */
export enum GroupSessionRole {
  OWNER = 'owner',           // Full control over the session
  FACILITATOR = 'facilitator', // Co-facilitator with edit rights
  DOCUMENTOR = 'documentor',   // Documentation focus
  PARTICIPANT = 'participant', // Limited participant access
  // Legacy roles
  EDITOR = 'editor',         // Being replaced by FACILITATOR
  VIEWER = 'viewer',         // Being replaced by PARTICIPANT
}

/**
 * Constants for GroupSessionRole
 * Used for type-safe comparisons and validation
 */
export const GROUP_SESSION_ROLES = {
  OWNER: 'owner' as const,
  FACILITATOR: 'facilitator' as const,
  DOCUMENTOR: 'documentor' as const,
  PARTICIPANT: 'participant' as const,
  // Legacy roles
  EDITOR: 'editor' as const,
  VIEWER: 'viewer' as const,
};

/**
 * Type guard for GroupSessionRole
 * Validates that a value is a valid group session role
 */
export function isValidGroupSessionRole(value: unknown): value is GroupSessionRole {
  if (typeof value !== 'string') return false;
  
  // Case-insensitive comparison for better compatibility
  const normalizedValue = value.toLowerCase();

  return Object.values(GROUP_SESSION_ROLES).includes(normalizedValue as GroupSessionRole);
}

/**
 * Represents a group session in the application
 * This interface matches the database schema structure for type consistency
 */
export interface GroupSession {
  id: string;
  title: string;
  name?: string; // For compatibility with components that expect 'name'
  description: string;
  creator_id?: string; // Not in current schema
  created_by?: string; // Alternative field name
  facilitator_name: string;
  status: GroupSessionStatus;
  created_at: string;
  updated_at: string;
  start_date: string;
  end_date: string;
  scheduled_for?: string; // Alternative field name for scheduling
  location: string;
  resources_needed: string;
  participant_count?: number;
  activity_count?: number;
  // Additional fields from database
  facilitator_notes?: string;
  settings?: Record<string, any>;
  participant_description?: string;
  participants_expected?: number;
  metadata?: Record<string, any>;
  max_participants?: number;
  tags?: string[]; // For session tags
  // Privacy and access control settings
  is_public?: boolean;
  allow_guest_viewing?: boolean;
  allow_participant_uploads?: boolean;
  require_approval?: boolean;
  notify_on_activity?: boolean;
  // For backward compatibility
  activities?: any[];
  // Image field for session flyers and social media
  image_url?: string;
  /**
   * Optional field for UI display to show the user's role in this session
   * This is not stored in the database but added by frontend hooks
   */
  userRole?: GroupSessionRole | string;
}

/**
 * Form data structure for creating or updating a group session
 */
export interface GroupSessionFormData {
  title: string;
  description: string;
  facilitator_name: string;
  start_date: string;
  end_date: string;
  location: string;
  resources_needed: string;
  // Additional optional fields
  facilitator_notes?: string;
  participant_description?: string;
  participants_expected?: number;
  settings?: Record<string, any>;
  image_url?: string;
}

/**
 * Filter options for group session queries
 */
export interface GroupSessionFilter {
  status?: string;
  search?: string;
  facilitator?: string;
  date_range?: {
    start?: string;
    end?: string;
  };
}

/**
 * Group Session Member interface
 * Represents a user's membership in a group session
 */
export interface GroupSessionMember {
  id: string;
  group_session_id: string;
  user_id: string;
  role: GroupSessionRole;
  joined_at: string;
  invited_by?: string;
  user?: {
    id: string;
    email: string;
    display_name?: string;
    avatar_url?: string;
  };
}
